import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { extractError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingState, EmptyState } from "@/components/Common";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { CaseDialog } from "@/components/CaseTaskDialogs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CASE_STATUSES, PRIORITIES } from "@/lib/constants";
import { fmtDate } from "@/lib/format";
import { FolderKanban, Search, FolderPlus, ArrowUpNarrowWide, ArrowDownWideNarrow, Download, FileSpreadsheet, FileText, Inbox } from "lucide-react";
import { VoiceSearchButton, appendSearch } from "@/components/VoiceSearchButton";
import { toast } from "sonner";

// Ordinamenti disponibili e direzione di default sensata per ciascuno
const SORT_OPTIONS = [
  { value: "data", label: "Data inizio relazione", dir: "desc" },
  { value: "nominativo", label: "Nominativo", dir: "asc" },
  { value: "assegnatario", label: "Assegnatario", dir: "asc" },
  { value: "priorita", label: "Priorità", dir: "desc" },
];

const Cases = () => {
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const [searchParams] = useSearchParams();

  // Enti associati all'utente corrente (per l'opzione "I miei enti")
  const myEnti = useMemo(() => {
    const s = new Set([...(user?.enti_ids || []), ...(user?.ente_id ? [user.ente_id] : [])]);
    return [...s];
  }, [user]);

  // Default filtro Ente: se l'utente ha enti associati e non arriva da un deep-link con
  // uno scope specifico, apre già filtrato su "I miei enti".
  const initialEnte = (() => {
    if (searchParams.get("ente_id")) return searchParams.get("ente_id");
    const hasScope = ["stato", "priorita", "servizio_id", "non_assegnate", "q"].some((k) => searchParams.get(k));
    if (!hasScope && myEnti.length > 0) return "__mine__";
    return "";
  })();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [stato, setStato] = useState(searchParams.get("stato") || "");
  const [priorita, setPriorita] = useState(searchParams.get("priorita") || "");
  const [services, setServices] = useState([]);
  const [servizio, setServizio] = useState(searchParams.get("servizio_id") || "");
  const [entities, setEntities] = useState([]);
  const [ente, setEnte] = useState(initialEnte);
  const [nonAssegnate, setNonAssegnate] = useState(searchParams.get("non_assegnate") === "true");
  const [ordina, setOrdina] = useState("data");
  const [direzione, setDirezione] = useState("desc");
  const [exporting, setExporting] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => { api.get("/services").then(({ data }) => setServices(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/entities").then(({ data }) => setEntities(data)).catch(() => {}); }, []);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (stato) params.set("stato", stato);
    if (priorita) params.set("priorita", priorita);
    if (servizio) params.set("servizio_id", servizio);
    if (ente === "__mine__") { if (myEnti.length) params.set("ente_ids", myEnti.join(",")); }
    else if (ente) params.set("ente_id", ente);
    if (nonAssegnate) params.set("non_assegnate", "true");
    params.set("ordina", ordina);
    params.set("direzione", direzione);
    return params;
  }, [q, stato, priorita, servizio, ente, myEnti, nonAssegnate, ordina, direzione]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/cases?${buildParams().toString()}`); setItems(data.items); setTotal(data.total); } catch (e) { console.error("Errore caricamento pratiche", e); }
    finally { setLoading(false); }
  }, [buildParams]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  // Cambio criterio di ordinamento: applica la direzione di default associata
  const changeOrdina = (v) => {
    setOrdina(v);
    const opt = SORT_OPTIONS.find((o) => o.value === v);
    if (opt) setDirezione(opt.dir);
  };
  const toggleDirezione = () => setDirezione((d) => (d === "asc" ? "desc" : "asc"));

  const exportData = async (formato) => {
    setExporting(true);
    try {
      const params = buildParams();
      params.set("formato", formato);
      const r = await api.get(`/cases/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = formato === "xlsx" ? "richieste_pratiche.xlsx" : "richieste_pratiche.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Esportazione ${formato.toUpperCase()} generata`);
    } catch (e) { toast.error(extractError(e, "Esportazione non riuscita")); }
    finally { setExporting(false); }
  };

  return (
    <div>
      <PageHeader icon={FolderKanban} title="Richieste e pratiche" description={`${total} pratiche`}
        actions={
          <>
            {can("reports.export") && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={exporting || items.length === 0} data-testid="cases-export-button">
                    <Download className="h-4 w-4 mr-2" /> Esporta
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => exportData("xlsx")} data-testid="cases-export-xlsx">
                    <FileSpreadsheet className="h-4 w-4 mr-2 text-[color:var(--brand-green-700)]" /> Excel (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportData("csv")} data-testid="cases-export-csv">
                    <FileText className="h-4 w-4 mr-2 text-[color:var(--brand-blue-600)]" /> CSV (.csv)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {can("cases.write") && <Button onClick={() => setOpen(true)} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="new-case-button"><FolderPlus className="h-4 w-4 mr-2" /> Nuova pratica</Button>}
          </>
        } />

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nominativo, numero o descrizione…" className="pl-9 pr-11" data-testid="cases-search" />
          <div className="absolute right-2 top-1/2 -translate-y-1/2"><VoiceSearchButton onResult={(t) => setQ((p) => appendSearch(p, t))} data-testid="cases-search-mic" /></div>
        </div>
        <Select value={stato || "tutti"} onValueChange={(v) => setStato(v === "tutti" ? "" : v)}>
          <SelectTrigger className="w-[170px]" data-testid="cases-filter-stato"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent><SelectItem value="tutti">Tutti gli stati</SelectItem>{CASE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={priorita || "tutte"} onValueChange={(v) => setPriorita(v === "tutte" ? "" : v)}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Priorità" /></SelectTrigger>
          <SelectContent><SelectItem value="tutte">Tutte le priorità</SelectItem>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={servizio || "tutti"} onValueChange={(v) => setServizio(v === "tutti" ? "" : v)}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="Servizio" /></SelectTrigger>
          <SelectContent className="max-h-64"><SelectItem value="tutti">Tutti i servizi</SelectItem>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={ente || "tutti"} onValueChange={(v) => setEnte(v === "tutti" ? "" : v)}>
          <SelectTrigger className="w-[200px]" data-testid="cases-filter-ente"><SelectValue placeholder="Ente" /></SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="tutti">Tutti gli enti</SelectItem>
            {myEnti.length > 0 && <SelectItem value="__mine__" data-testid="cases-filter-ente-mine">I miei enti</SelectItem>}
            {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.denominazione}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant={nonAssegnate ? "default" : "outline"} onClick={() => setNonAssegnate((v) => !v)}
          className={nonAssegnate ? "bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" : ""}
          data-testid="cases-filter-non-assegnate">
          <Inbox className="h-4 w-4 mr-2" /> Non assegnate
        </Button>
      </div>

      {/* Ordinamento */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm text-muted-foreground">Ordina per</span>
        <Select value={ordina} onValueChange={changeOrdina}>
          <SelectTrigger className="w-[210px] h-9" data-testid="cases-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>{SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="h-9" onClick={toggleDirezione} data-testid="cases-sort-direction"
          title={direzione === "asc" ? "Crescente" : "Decrescente"}>
          {direzione === "asc" ? <ArrowUpNarrowWide className="h-4 w-4" /> : <ArrowDownWideNarrow className="h-4 w-4" />}
          <span className="ml-1 hidden sm:inline">{direzione === "asc" ? "Crescente" : "Decrescente"}</span>
        </Button>
      </div>

      {loading ? <LoadingState /> : items.length === 0 ? <EmptyState title="Nessuna pratica trovata" /> : (
        <Card className="divide-y overflow-hidden">
          {items.map((c) => {
            const dataInizio = c.data_inizio_relazione || c.created_at;
            return (
              <button key={c.id} onClick={() => navigate(`/app/pratiche/${c.id}`)} className="w-full text-left p-4 hover:bg-muted transition-colors" data-testid="case-row">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900" data-testid="case-row-title">
                      <span className="text-[color:var(--brand-blue-700)]">{fmtDate(dataInizio)}</span>
                      <span className="mx-1.5 text-slate-300">·</span>
                      {c.numero}
                      <span className="text-muted-foreground font-normal"> · {c.persona_nome || "-"}</span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {c.servizio_nome}
                      {c.ente_nome && <> · <span className="text-[color:var(--brand-blue-700)]" data-testid="case-ente">{c.ente_nome}</span></>}
                      {c.gestore_nome && <> · <span className="font-semibold text-[color:var(--brand-green-700)]" data-testid="case-gestore">Gestore: {c.gestore_nome}</span></>}
                    </div>
                  </div>
                  <div className="flex gap-2 items-center"><PriorityBadge priorita={c.priorita} /><StatusBadge stato={c.stato} /></div>
                </div>
              </button>
            );
          })}
        </Card>
      )}
      <CaseDialog open={open} setOpen={setOpen} personId={null} onDone={(c) => navigate(`/app/pratiche/${c.id}`)} />
    </div>
  );
};

export default Cases;
